import { mergeCookies, parseSetCookie, type PwCookie } from '../set-cookie-merge.js';

export interface NativeRequestCookieJar {
  cookies(requestUrl: string): Promise<readonly PwCookie[]>;
  applySetCookies(cookies: readonly PwCookie[], requestUrl: string): Promise<void>;
}

export interface NativeRequestFetchResult {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string | null;
}

type HeadersWithSetCookie = Headers & {
  getSetCookie?: () => string[];
};

function getSetCookieHeaders(headers: Headers): string[] {
  const withSetCookie = headers as HeadersWithSetCookie;
  if (typeof withSetCookie.getSetCookie === 'function') {
    return withSetCookie.getSetCookie().filter((value) => value.trim().length > 0);
  }

  const singleHeader = headers.get('set-cookie');
  return singleHeader ? [singleHeader] : [];
}

function responseHeadersWithoutSetCookie(headers: Headers): Record<string, string> {
  const result: Record<string, string> = {};
  headers.forEach((value, key) => {
    if (key.toLowerCase() !== 'set-cookie') {
      result[key] = value;
    }
  });
  return result;
}

function cookieDomainMatches(requestHost: string, cookieDomain: string): boolean {
  const normalized = cookieDomain.replace(/^\./, '').toLowerCase();
  return requestHost === normalized || requestHost.endsWith(`.${normalized}`);
}

function cookiePathMatches(requestPath: string, cookiePath: string): boolean {
  if (requestPath === cookiePath) return true;
  if (!requestPath.startsWith(cookiePath)) return false;
  return cookiePath.endsWith('/') || requestPath.charAt(cookiePath.length) === '/';
}

function isCookieExpired(cookie: PwCookie): boolean {
  return cookie.expires > 0 && cookie.expires <= Math.floor(Date.now() / 1000);
}

function cookiesForRequest(cookies: readonly PwCookie[], requestUrl: string): PwCookie[] {
  let url: URL;
  try {
    url = new URL(requestUrl);
  } catch {
    return [];
  }

  return cookies.filter((cookie) => {
    if (isCookieExpired(cookie)) return false;
    if (cookie.secure && url.protocol !== 'https:') return false;
    if (!cookieDomainMatches(url.hostname.toLowerCase(), cookie.domain)) return false;
    return cookiePathMatches(url.pathname || '/', cookie.path || '/');
  });
}

function buildCookieHeader(cookies: readonly PwCookie[], requestUrl: string): string | null {
  const matching = cookiesForRequest(cookies, requestUrl);
  if (matching.length === 0) return null;
  return matching.map((cookie) => `${cookie.name}=${cookie.value}`).join('; ');
}

async function applyResponseCookies(
  response: Response,
  requestUrl: string,
  cookieJar: NativeRequestCookieJar
): Promise<void> {
  const parsed = getSetCookieHeaders(response.headers)
    .map((raw) => parseSetCookie(raw, requestUrl))
    .filter((cookie): cookie is PwCookie => cookie !== null);

  if (parsed.length > 0) {
    await cookieJar.applySetCookies(parsed, requestUrl);
  }
}

function shouldFollowRedirect(status: number): boolean {
  return status === 301 || status === 302 || status === 303 || status === 307 || status === 308;
}

function redirectedMethod(args: { status: number; method: string; body?: string }): {
  method: string;
  body?: string;
} {
  const method = args.method.toUpperCase();
  const convertsToGet =
    args.status === 303 ||
    ((args.status === 301 || args.status === 302) && method !== 'GET' && method !== 'HEAD');
  if (convertsToGet) {
    return { method: 'GET' };
  }
  return { method, body: args.body };
}

function requestHeaders(args: {
  defaultHeaders?: Record<string, string>;
  baseHeaders?: Record<string, string>;
  body?: string;
  cookieHeader: string | null;
}): Headers {
  const headers = new Headers(args.defaultHeaders ?? {});
  for (const [name, value] of Object.entries(args.baseHeaders ?? {})) {
    headers.set(name, value);
  }
  if (args.body === undefined) {
    headers.delete('content-length');
    headers.delete('content-type');
  }
  if (!headers.has('cookie') && args.cookieHeader) {
    headers.set('cookie', args.cookieHeader);
  }
  return headers;
}

/**
 * Fetch using the platform fetch implementation while explicitly mirroring the
 * browser context's cookie jar. This avoids Playwright APIRequestContext's
 * Bun-specific Set-Cookie parser bug where ClientResponse.url can be relative
 * even for an absolute request URL.
 */
export async function fetchWithNativeRequestCookies(args: {
  requestUrl: string;
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  timeoutMs?: number;
  signal?: AbortSignal;
  cookieJar: NativeRequestCookieJar;
  /** Headers derived for each redirect target (for origin-scoped credentials). */
  defaultHeadersForUrl?: (requestUrl: string) => Record<string, string> | undefined;
  maxRedirects?: number;
}): Promise<NativeRequestFetchResult> {
  const maxRedirects = args.maxRedirects ?? 20;
  let requestUrl = args.requestUrl;
  const initialOrigin = new URL(requestUrl).origin;
  let method = (args.method ?? 'GET').toUpperCase();
  let body = args.body;

  for (let redirectCount = 0; redirectCount <= maxRedirects; redirectCount += 1) {
    const cookies = await args.cookieJar.cookies(requestUrl);
    const cookieHeader = buildCookieHeader(cookies, requestUrl);
    const headers = requestHeaders({
      defaultHeaders: args.defaultHeadersForUrl?.(requestUrl),
      // Step headers can contain tokens resolved from credential extractors or
      // templates. Preserve them on same-origin redirects, never across an
      // origin boundary.
      baseHeaders: new URL(requestUrl).origin === initialOrigin ? args.headers : undefined,
      body,
      cookieHeader,
    });

    const timeoutController = args.timeoutMs === undefined ? null : new AbortController();
    const timeout =
      args.timeoutMs === undefined
        ? null
        : setTimeout(() => timeoutController?.abort(), args.timeoutMs);
    const signals: AbortSignal[] = [];
    if (args.signal) signals.push(args.signal);
    if (timeoutController) signals.push(timeoutController.signal);
    const signal =
      signals.length === 0
        ? undefined
        : signals.length === 1
          ? signals[0]
          : AbortSignal.any(signals);
    let response: Response;
    try {
      response = await fetch(requestUrl, {
        method,
        headers,
        body,
        redirect: 'manual',
        signal,
      });
    } finally {
      if (timeout) clearTimeout(timeout);
    }

    await applyResponseCookies(response, requestUrl, args.cookieJar);

    if (shouldFollowRedirect(response.status)) {
      const location = response.headers.get('location');
      if (!location) {
        return {
          status: response.status,
          statusText: response.statusText,
          headers: responseHeadersWithoutSetCookie(response.headers),
          body: await response.text().catch(() => null),
        };
      }
      if (redirectCount === maxRedirects) {
        throw new Error(`Max redirect count exceeded for ${args.requestUrl}`);
      }

      requestUrl = new URL(location, requestUrl).toString();
      const redirected = redirectedMethod({ status: response.status, method, body });
      method = redirected.method;
      body = redirected.body;
      continue;
    }

    return {
      status: response.status,
      statusText: response.statusText,
      headers: responseHeadersWithoutSetCookie(response.headers),
      body: await response.text().catch(() => null),
    };
  }

  throw new Error(`Max redirect count exceeded for ${args.requestUrl}`);
}

export function mergeNativeRequestCookies(
  existing: readonly PwCookie[],
  incoming: readonly PwCookie[]
): PwCookie[] {
  return mergeCookies(existing, incoming);
}
